import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import SectionTitle from '../components/common/SectionTitle';
import CardSoft from '../components/ui/CardSoft';
import { motion } from 'framer-motion';

// Static blogs that will always be shown
const staticBlogs = [
    {
        id: 'static-1',
        title: "Why We Ignore Health Concerns and Why We Shouldn't",
        excerpt: "Explore the psychological and social factors that lead us to delay seeking medical help, and understand why early intervention matters for your long-term health.",
        content: "Many people tend to ignore health concerns, often dismissing symptoms as minor or temporary. This behavior stems from various psychological and social factors, including fear of diagnosis, busy lifestyles, and cultural stigmas around seeking medical help.\n\nHowever, early intervention is crucial for long-term health. Ignoring symptoms can lead to complications that could have been easily prevented with timely medical attention. Whether it's a persistent pain, unusual skin changes, or other bodily concerns, addressing them early can make a significant difference in treatment outcomes.\n\nUnderstanding why we avoid seeking help is the first step toward changing this behavior. By recognizing these patterns, we can make more informed decisions about our health and well-being.",
        author: "Dr. Nyssa Health Team",
        category: "Health Awareness",
        imageUrl: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
        createdAt: new Date('2024-01-10'),
        isStatic: true
    },
    {
        id: 'static-2',
        title: "When Is It Time to See a Doctor for Intimate Health Issues?",
        excerpt: "Learn to recognize the signs and symptoms that indicate it's time to seek professional help for intimate health concerns, without fear or embarrassment.",
        content: "Intimate health issues can be uncomfortable to discuss, but they're important aspects of overall well-being. Many people delay seeking medical help due to embarrassment or fear of judgment, but healthcare professionals are trained to handle these concerns with sensitivity and professionalism.\n\nSigns that you should consult a doctor include persistent pain, unusual discharge, changes in skin texture or color, lumps or bumps, bleeding between periods, or any other symptoms that concern you. Remember, early detection and treatment of intimate health issues can prevent serious complications.\n\nYour health and comfort are paramount. Don't let embarrassment prevent you from getting the care you need. Medical professionals have seen it all and are there to help, not judge.",
        author: "Dr. Nyssa Health Team",
        category: "Intimate Health",
        imageUrl: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
        createdAt: new Date('2024-01-09'),
        isStatic: true
    },
    {
        id: 'static-3',
        title: "Understanding Skin Changes: What's Normal and What's Not",
        excerpt: "Discover how to differentiate between normal skin changes and those that require medical attention, empowering you to take charge of your skin health.",
        content: "Our skin changes throughout our lives due to aging, hormones, sun exposure, and various other factors. While many changes are completely normal, some may indicate underlying health issues that require medical attention.\n\nNormal skin changes include age spots, fine lines, and changes in texture due to aging. However, you should consult a doctor if you notice sudden changes in moles, persistent rashes, unusual growths, changes in skin color, or any lesions that don't heal.\n\nRegular skin self-examinations are important for early detection of potential issues. Pay attention to the ABCDE rule for moles: Asymmetry, Border irregularity, Color variation, Diameter larger than 6mm, and Evolution or change over time.\n\nTaking care of your skin through proper sun protection, hydration, and a healthy lifestyle can help maintain skin health and make it easier to spot when something is amiss.",
        author: "Dr. Nyssa Health Team",
        category: "Skin Health",
        imageUrl: "https://images.unsplash.com/photo-1515377905703-c4788e51af15?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
        createdAt: new Date('2024-01-08'),
        isStatic: true
    },
    {
        id: 'static-4',
        title: "Post-Pregnancy Body Changes Nobody Talks About",
        excerpt: "An honest discussion about the physical and emotional changes that occur after childbirth, and the support and treatments available to help you feel like yourself again.",
        content: "Pregnancy and childbirth bring profound changes to a woman's body, many of which persist long after delivery. While stretch marks and weight changes are commonly discussed, many other changes remain taboo topics, leaving new mothers feeling isolated and unprepared.\n\nCommon post-pregnancy changes include pelvic floor weakness, changes in breast size and shape, skin pigmentation changes, hair loss, and intimate area changes. These are all normal and experienced by many women, yet rarely discussed openly.\n\nIt's important to know that help is available. Pelvic floor therapy, cosmetic treatments, and various medical interventions can address many of these concerns. More importantly, talking openly with healthcare providers about these changes is crucial for both physical and emotional well-being.\n\nRemember, your body has done something incredible. While it's natural to want to feel like yourself again, be patient and kind to yourself during this transition. Seek support when needed, and know that you're not alone in experiencing these changes.",
        author: "Dr. Nyssa Health Team",
        category: "Women's Health",
        imageUrl: "https://images.unsplash.com/photo-1555252333-9f8e92e65df9?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
        createdAt: new Date('2024-01-07'),
        isStatic: true
    },
    {
        id: 'static-5',
        title: "How Stress Affects Skin, Hair and Overall Health",
        excerpt: "Understand the profound impact stress has on your body, from hair loss to skin conditions, and learn practical strategies to manage stress for better health.",
        content: "Stress is more than just a mental state—it has tangible effects on your physical health, particularly visible in your skin and hair. When you're stressed, your body releases cortisol and other stress hormones that can trigger various physical responses.\n\nCommon stress-related issues include acne breakouts, eczema flare-ups, psoriasis, hair loss (telogen effluvium), premature graying, and accelerated aging signs. Stress can also worsen existing skin conditions and slow down healing processes.\n\nThe good news is that managing stress can significantly improve these conditions. Effective stress management techniques include regular exercise, adequate sleep, meditation and mindfulness practices, maintaining social connections, and seeking professional help when needed.\n\nAdditionally, maintaining a consistent skincare routine, eating a balanced diet rich in antioxidants, staying hydrated, and protecting your skin from sun damage can help mitigate stress's physical effects.\n\nRemember, taking care of your mental health is just as important as taking care of your physical health. They're intimately connected, and addressing one often helps the other.",
        author: "Dr. Nyssa Health Team",
        category: "Wellness",
        imageUrl: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
        createdAt: new Date('2024-01-06'),
        isStatic: true
    }
];

const BlogsPage = () => {
    const [blogs, setBlogs] = useState(staticBlogs); // Start with static blogs
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchBlogs();
    }, []);

    const fetchBlogs = async () => {
        try {
            const blogsCollection = collection(db, 'blogs');
            const q = query(blogsCollection, where('status', '==', 'published'));
            const blogsSnapshot = await getDocs(q);
            const firebaseBlogs = blogsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            console.log('Firebase blogs fetched:', firebaseBlogs.length);
            console.log('Firebase blogs:', firebaseBlogs);

            // Always merge Firebase blogs with static blogs
            const allBlogs = [...firebaseBlogs, ...staticBlogs];

            // Remove duplicates based on title
            const uniqueBlogs = allBlogs.filter((blog, index, self) =>
                index === self.findIndex((b) => b.title === blog.title)
            );

            // Sort by date (newest first)
            const sortedBlogs = uniqueBlogs.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
                const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
                return dateB - dateA;
            });

            console.log('Total blogs after merge:', sortedBlogs.length);
            setBlogs(sortedBlogs);
        } catch (error) {
            console.error('Error fetching blogs:', error);
            // If Firebase fails, keep static blogs
            setBlogs(staticBlogs);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const handleBlogClick = (blog) => {
        if (blog.isStatic) {
            // For static blogs, pass the blog data via state
            navigate(`/blogs/${blog.id}`, { state: { blog } });
        } else {
            // For Firebase blogs, navigate normally
            navigate(`/blogs/${blog.id}`);
        }
    };

    if (loading) {
        return (
            <div style={{
                paddingTop: '100px',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: '50px',
                        height: '50px',
                        border: '4px solid #f0f0f0',
                        borderTop: '4px solid #539CED',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 20px'
                    }}></div>
                    <p>Loading blogs...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ paddingTop: '100px' }}>
            {/* Hero Section */}
            <div style={{
                backgroundColor: '#c99d99',
                color: 'var(--secondary)',
                padding: '80px 0',
                textAlign: 'center',
                marginBottom: '60px'
            }}>
                <div className="container">
                    <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '3rem', marginBottom: '20px' }}>Our Blogs</h1>
                    <p style={{ maxWidth: '700px', margin: '0 auto', fontSize: '1.2rem', color: 'var(--text-light)' }}>
                        Insights, updates, and educational content from our experts.
                    </p>
                </div>
            </div>

            <div className="container" style={{ marginBottom: '100px' }}>
                {blogs.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                        <p style={{ fontSize: '1.2rem', color: '#666' }}>No blogs available at the moment. Check back soon!</p>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                        gap: '40px'
                    }}>
                        {blogs.map((blog, index) => (
                            <motion.div
                                key={blog.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <CardSoft style={{
                                    padding: 0,
                                    overflow: 'hidden',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    cursor: 'pointer',
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    backgroundColor: 'var(--white)'
                                }}
                                    onClick={() => handleBlogClick(blog)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#faf8f8';
                                        e.currentTarget.style.transform = 'translateY(-8px)';
                                        e.currentTarget.style.boxShadow = '0 20px 40px rgba(181, 131, 131, 0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--white)';
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.08)';
                                    }}
                                >
                                    {(blog.imageUrl || blog.image) && (
                                        <div style={{ width: '100%', height: '250px', overflow: 'hidden' }}>
                                            <img
                                                src={blog.imageUrl || blog.image}
                                                alt={blog.title}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    display: 'block',
                                                    transition: 'transform 0.5s ease'
                                                }}
                                                onMouseOver={(e) => e.target.style.transform = 'scale(1.1)'}
                                                onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                                            />
                                        </div>
                                    )}
                                    <div style={{ padding: '30px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        {blog.createdAt && (
                                            <span style={{
                                                fontSize: '0.85rem',
                                                color: 'var(--primary)',
                                                fontWeight: '600',
                                                marginBottom: '10px',
                                                display: 'block'
                                            }}>
                                                {formatDate(blog.createdAt)}
                                            </span>
                                        )}
                                        {blog.category && (
                                            <span style={{
                                                fontSize: '0.75rem',
                                                color: '#ED8C5A',
                                                fontWeight: '600',
                                                marginBottom: '10px',
                                                display: 'inline-block',
                                                textTransform: 'uppercase',
                                                letterSpacing: '1px'
                                            }}>
                                                {blog.category}
                                            </span>
                                        )}
                                        <h3 style={{
                                            fontFamily: 'var(--font-heading)',
                                            fontSize: '1.4rem',
                                            marginBottom: '15px',
                                            color: 'var(--secondary)'
                                        }}>
                                            {blog.title}
                                        </h3>
                                        <p style={{
                                            color: 'var(--text-light)',
                                            lineHeight: '1.6',
                                            marginBottom: '20px',
                                            flex: 1
                                        }}>
                                            {blog.excerpt || blog.content?.substring(0, 150) + '...'}
                                        </p>
                                        {blog.author && (
                                            <p style={{
                                                fontSize: '0.85rem',
                                                color: '#888',
                                                marginBottom: '15px',
                                                fontStyle: 'italic'
                                            }}>
                                                By {blog.author}
                                            </p>
                                        )}
                                        <button style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'var(--primary)',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            padding: 0,
                                            fontSize: '0.9rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px'
                                        }}>
                                            Read More →
                                        </button>
                                    </div>
                                </CardSoft>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogsPage;

